interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Human verification could not load."));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export async function turnstileToken(
  container: HTMLElement,
  action = "scan",
): Promise<string | null> {
  const response = await fetch("/api/v1/config");
  const config = await response.json();
  if (!response.ok)
    throw new Error("Human verification configuration could not be loaded.");
  if (!config.turnstile_required) return null;
  if (!config.turnstile_site_key)
    throw new Error("Human verification is temporarily unavailable.");
  await loadScript();
  if (!window.turnstile)
    throw new Error("Human verification did not initialize.");

  return new Promise((resolve, reject) => {
    let widgetId = "";
    const finish = (callback: () => void) => {
      clearTimeout(timeout);
      if (widgetId) window.turnstile?.remove(widgetId);
      callback();
    };
    const timeout = window.setTimeout(
      () =>
        finish(() =>
          reject(new Error("Human verification timed out. Please retry.")),
        ),
      15_000,
    );
    widgetId = window.turnstile!.render(container, {
      sitekey: config.turnstile_site_key,
      action,
      execution: "execute",
      appearance: "interaction-only",
      callback: (token: string) => finish(() => resolve(token)),
      "error-callback": () => {
        finish(() =>
          reject(new Error("Human verification failed. Please retry.")),
        );
        return true;
      },
      "expired-callback": () => {
        finish(() =>
          reject(new Error("Human verification expired. Please retry.")),
        );
        return true;
      },
    });
    window.turnstile!.execute(widgetId);
  });
}
